import React, { useEffect, useState } from "react";
import { Container, Modal } from "react-bootstrap";
import moment from "moment";

const STORAGE_KEY = "accountExpiryMessages";

const AccountExpiryNotifyModal = ({ message = {}, show, closeModal, blocked = false }) => {
  const [shouldShow, setShouldShow] = useState(show);

  const getMessageKey = (msg, type) => {
    return `${msg?.username || "user"}_${type}_${moment().format(
      "YYYY-MM-DD"
    )}`;
  };

  useEffect(() => {

    // to show blocked message
    if (show && blocked) {
      setShouldShow(true);
      return;
    }

    if (!show || !message) return;

    let type = null;
    let key = null;

    const { expiryDiff, trialDiff, allowedDaysLeft, isPastAllowedDate } =
      message;
    const today = new Date().getDate();

    if (isPastAllowedDate && trialDiff < 0) {
      type = "exit";
    } else if (expiryDiff < 0 && trialDiff >= 0 && trialDiff <= 14) {
      type = "once";
    } else if (
      trialDiff < 0 &&
      expiryDiff < 0 &&
      allowedDaysLeft >= 14 &&
      allowedDaysLeft < 30
    ) {
      type = "once";
    } else if (
      trialDiff < 0 &&
      expiryDiff < 0 &&
      allowedDaysLeft >= 0 &&
      allowedDaysLeft < 14
    ) {
      type = "session";
    } else if (expiryDiff >= 0 && expiryDiff <= 7) {
      type = "once";
    } else if (expiryDiff > 7 && expiryDiff <= 30 && today % 2 === 0) {
      type = "evenDay";
    }

    if (type === "once") {
      key = getMessageKey(message, "once");
      const shownKeys = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (shownKeys.includes(key)) {
        setShouldShow(false);
        return;
      }
    }

    if (type === "session") {
      key = getMessageKey(message, "session");
      const shownKeys = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
      if (shownKeys.includes(key)) {
        setShouldShow(false);
        return;
      }
    }

    if (type === "evenDay") {
      key = getMessageKey(message, "evenDay");
      const shownKeys = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (shownKeys.includes(key)) {
        setShouldShow(false);
        return;
      }
    }

    setShouldShow(true);
  }, [message?.username, blocked]);

  const handleClose = () => {

    if (blocked) {
      setShouldShow(false);
      closeModal();
      return
    }
    let type = "once";
    let key = null;
    const today = new Date().getDate();
    const { expiryDiff, trialDiff, allowedDaysLeft, isPastAllowedDate } =
      message;

    if (isPastAllowedDate && trialDiff < 0) type = "once";
    else if (expiryDiff < 0 && trialDiff >= 0 && trialDiff <= 14) type = "once";
    else if (
      trialDiff < 0 &&
      expiryDiff < 0 &&
      allowedDaysLeft >= 14 &&
      allowedDaysLeft < 30
    )
      type = "once";
    else if (
      trialDiff < 0 &&
      expiryDiff < 0 &&
      allowedDaysLeft >= 0 &&
      allowedDaysLeft < 14
    )
      type = "session";
    else if (expiryDiff >= 0 && expiryDiff <= 7) type = "once";
    else if (expiryDiff > 7 && expiryDiff <= 30 && today % 2 === 0)
      type = "evenDay";

    key = `${message?.username || "user"}_${type}_${moment().format(
      "YYYY-MM-DD"
    )}`;

    if (type === "once" || type === "evenDay") {
      const shownKeys = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (!shownKeys.includes(key)) {
        shownKeys.push(key);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(shownKeys));
      }
    }

    if (type === "session") {
      const shownKeys = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
      if (!shownKeys.includes(key)) {
        shownKeys.push(key);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(shownKeys));
      }
    }

    if (type === "exit") {
      localStorage.removeItem("STORAGE_KEY");
    }

    setShouldShow(false);
    closeModal();
  };

  const getMessage = ({
    username,
    expiryDate,
    trialDate,
    supportNo,
    expiryDiff,
    trialDiff,
    isPastAllowedDate,
    allowedDaysLeft = 0,
  } = {}) => {

    const formattedExpiryDate = moment(expiryDate, "DD/MM/YYYY")?.format(
      "MMM/YYYY"
    );
    const formattedTrialDate = moment(trialDate, "DD/MM/YYYY")?.format(
      "MMM/YYYY"
    );

    // Grace ended → Block access
    if (isPastAllowedDate && trialDiff < 0) {
      return (
        <>
          Your Locator account: {username || "--"} expired{" "}
          {expiryDate !== "31/01/1970" ? `on ${formattedExpiryDate}` : ""}, and
          your grace period ended on {formattedTrialDate}.
          <br />
          To restore access, please contact our team at{" "}
          <a href="mailto:info@locator.ae">info@locator.ae</a> or via WhatsApp
          at <a href={`https://wa.me/${supportNo}`}>{supportNo}</a>.
        </>
      );
    }

    // Expired but still in grace period
    if (expiryDiff < 0 && trialDiff >= 0 && trialDiff <= 14) {
      // trialDiff = days left in grace
      return (
        <>
          Your Locator account {username} expired on {formattedExpiryDate}.
          <br />
          However, your access remains active for {trialDiff} more day(s)
          <br />
          To ensure uninterrupted access, please contact our team at{" "}
          <a href="mailto:info@locator.ae">info@locator.ae</a> or via WhatsApp
          at <a href={`https://wa.me/${supportNo}`}>{supportNo}</a>.
        </>
      );
    }

    if (trialDiff < 0 && expiryDiff < 0) {
      // expired but still in allowed date : 30 days before : every session.
      if (allowedDaysLeft >= 14 && allowedDaysLeft < 30) {
        return (
          <>
            Your Locator account: {username} expired on {formattedExpiryDate}{" "}
            <br />
            However, your access remains active for {trialDiff < 0 ? 0 : trialDiff} more day(s)
            <br />
            To ensure uninterrupted access, please contact our team at{" "}
            <a href="mailto:info@locator.ae">info@locator.ae</a> or via WhatsApp
            at <a href={`https://wa.me/${supportNo}`}>{supportNo}</a>.
          </>
        );
      }

      // expired but still in allowed date : 14 days before : every session.
      if (allowedDaysLeft >= 0 && allowedDaysLeft < 14) {
        return (
          <>
            Your Locator account: {username} expired on {formattedExpiryDate}{" "}
            <br />
            However, your access remains active for {allowedDaysLeft} more
            day(s)
            <br />
            To ensure uninterrupted access, please contact our team at{" "}
            <a href="mailto:info@locator.ae">info@locator.ae</a> or via WhatsApp
            at <a href={`https://wa.me/${supportNo}`}>{supportNo}</a>.
          </>
        );
      }
    }

    // Before expiry: 7-day window → daily
    if (expiryDiff >= 0 && expiryDiff <= 7) {
      return (
        <>
          Your Locator account {username} is scheduled to expire in {expiryDiff}{" "}
          day(s).
          <br />
          To maintain uninterrupted access, please contact our team at{" "}
          <a href="mailto:info@locator.ae">info@locator.ae</a> or via WhatsApp
          at <a href={`https://wa.me/${supportNo}`}>{supportNo}</a>.
        </>
      );
    }

    // Before expiry: 30-day window → every even day
    if (expiryDiff > 7 && expiryDiff <= 30) {
      const today = new Date().getDate();
      if (today % 2 === 0) {
        return (
          <>
            Your Locator account {username} is set to expire this month (
            {formattedExpiryDate}).
            <br />
            To maintain uninterrupted access, please contact our team at{" "}
            <a href="mailto:info@locator.ae">info@locator.ae</a> or via WhatsApp
            at <a href={`https://wa.me/${supportNo}`}>{supportNo}</a>.
          </>
        );
      }
    }

    // Fallback
    return (
      <>
        Your Locator account {username} is scheduled to expire. Please contact
        support at <a href="mailto:info@locator.ae">info@locator.ae</a> or
        WhatsApp at {supportNo}.
      </>
    );
  };

  const getBlockedMessage = () => (
    <>
      Your LOCATOR Software account is blocked due to overdue payments towards software subscription & ETISALAT Data charges. Contact support at  <a href="mailto:customercare@locator.ae">customercare@locator.ae</a> or
      04 354 7766 to resolve the overdue payment and restore access.
    </>
  )

  return (
    <Modal
      show={shouldShow}
      dialogClassName="modal-40w"
      onHide={handleClose}
      aria-labelledby="accountexpirynotifymodal"
    >
      <Modal.Header closeButton>
        <span className="text-left" style={{ width: "80%" }}>
          Alert :
        </span>
      </Modal.Header>

      <Modal.Body className="show-grid">
        <Container>
          <span style={styles.messageStyle}>{blocked ? getBlockedMessage() : getMessage(message)}</span>
        </Container>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          style={{
            fontSize: "12px",
            backgroundColor: "#007bff",
            color: "#ffffff",
          }}
          className="btn btn-small"
          onClick={handleClose}
        >
          OK
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const styles = {
  messageStyle: {
    fontSize: "13px",
    fontWeight: "medium",
    color: "#444",
  },
};

export default AccountExpiryNotifyModal;
